import axios from 'axios';
import type {
    PriceRule,
    CreatePriceRuleInput,
    SyncTemplate,
    SegmentationOptions,
    AggressivenessOptions,
    SimulateNewRuleRequest,
    SimulateNewRuleResponse,
    GranulationResponse,
    SimulateBulkRequest,
    SimulateBulkResponse,
    SimulateBulkByPostRequest,
    SimulateBulkByPostResponse,
    BulkCreateRequest,
    CascadeGranulation,
    CreateCascadeGranulationInput,
    UpdateCascadeGranulationInput,
    PriceCascade,
    CreatePriceCascadeInput,
    UpdatePriceCascadeInput,
    CreatePriceCascadesBatchInput,
    CreatePriceCascadesBatchResponse,
    PostsWithSlugsResponse,
    CascadeStepsConfig
} from '@/types/price-rules';

const PRICE_RULES_API_URL = import.meta.env.VITE_PRICE_RULES_API_URL || 'http://localhost:8082';

const priceRulesClient = axios.create({
    baseURL: PRICE_RULES_API_URL,
    headers: { 'Content-Type': 'application/json' },
});

priceRulesClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// ========== SEGMENTATION & ENUMS ==========

export const getSegmentationOptions = async (): Promise<SegmentationOptions> => {
    const response = await priceRulesClient.get<SegmentationOptions>('/api/v1/segmentation');
    return response.data;
};

export const getAggressivenessOptions = async (): Promise<AggressivenessOptions> => {
    const response = await priceRulesClient.get<AggressivenessOptions>('/api/v1/sync-aggressiveness');
    return response.data;
};

// ========== PRICE RULES API ==========

export const getAllPriceRules = async (): Promise<PriceRule[]> => {
    const response = await priceRulesClient.get('/api/v1/price-rules');
    return response.data;
};

/**
 * Lista regras de preço por domínio
 * GET /api/v1/price-rules/domain/{domainId}
 */
export const getPriceRules = async (domainId: number): Promise<PriceRule[]> => {
    const response = await priceRulesClient.get(`/api/v1/price-rules/domain/${domainId}`);
    return response.data;
};

export const getPriceRule = async (ruleId: number): Promise<PriceRule> => {
    const response = await priceRulesClient.get<PriceRule>(`/api/v1/price-rules/${ruleId}`);
    return response.data;
};

export const createPriceRule = async (data: CreatePriceRuleInput): Promise<PriceRule> => {
    const response = await priceRulesClient.post<PriceRule>('/api/v1/price-rules', data);
    return response.data;
};

export const updatePriceRule = async (ruleId: number, data: { price?: number; status?: string }): Promise<PriceRule> => {
    const response = await priceRulesClient.put<PriceRule>(`/api/v1/price-rules/${ruleId}`, data);
    return response.data;
};

export const deletePriceRule = async (ruleId: number): Promise<void> => {
    await priceRulesClient.delete(`/api/v1/price-rules/${ruleId}`);
};

export const getGranulation = async (): Promise<GranulationResponse> => {
    const response = await priceRulesClient.get<GranulationResponse>('/api/v1/price-rules/granulation');
    return response.data;
};

// ========== SIMULATION API ==========

export const simulateNewRule = async (data: SimulateNewRuleRequest): Promise<SimulateNewRuleResponse> => {
    const response = await priceRulesClient.post<SimulateNewRuleResponse>('/api/v1/price-rules/simulate', data);
    return response.data;
};

export const simulateBulkRules = async (data: SimulateBulkRequest): Promise<SimulateBulkResponse> => {
    const response = await priceRulesClient.post<SimulateBulkResponse>('/api/v1/price-rules/simulate-bulk', data);
    return response.data;
};

export const simulateBulkByPost = async (data: SimulateBulkByPostRequest): Promise<SimulateBulkByPostResponse> => {
    const response = await priceRulesClient.post<SimulateBulkByPostResponse>('/api/v1/price-rules/simulate-bulk-posts', data);
    return response.data;
};

export const bulkCreatePriceRules = async (data: BulkCreateRequest): Promise<any> => {
    const response = await priceRulesClient.post('/api/v1/price-rules/bulk', data);
    return response.data;
};

export const simulateExistingRule = async (ruleId: number): Promise<any> => {
    const response = await priceRulesClient.post(`/api/v1/price-rules/simulate/${ruleId}`);
    return response.data;
};


export const applySimulation = async (ruleId: number, force: boolean = false): Promise<any> => {
    const response = await priceRulesClient.post(`/api/v1/price-rules/${ruleId}/apply-simulation`, { force });
    return response.data;
};

// ========== SYNC TEMPLATES API ==========

export const getSyncTemplates = async (): Promise<SyncTemplate[]> => {
    const response = await priceRulesClient.get<SyncTemplate[]>('/api/v1/sync-templates');
    return response.data;
};

export const createSyncTemplate = async (data: any): Promise<SyncTemplate> => {
    const response = await priceRulesClient.post<SyncTemplate>('/api/v1/sync-templates', data);
    return response.data;
};



/**
 * Busca relatório de evolução de uma regra
 */
export const getRuleReport = async (
    ruleId: number,
    startDate: string,
    endDate: string
): Promise<RuleReportResponse> => {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });

    const response = await priceRulesClient.get(
        `/api/v1/price-rules/${ruleId}/report?${params}`
    );
    return response.data;
};

/**
 * Busca apenas dados hora a hora
 */
export const getRuleHourlyData = async (
    ruleId: number,
    startDate: string,
    endDate: string
): Promise<HourlyReportResponse> => {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });

    const response = await priceRulesClient.get(
        `/api/v1/price-rules/${ruleId}/report/hourly?${params}`
    );
    return response.data;
};

/**
 * Busca apenas logs de alterações
 */
export const getRuleLogs = async (
    ruleId: number,
    startDate: string,
    endDate: string
): Promise<LogsReportResponse> => {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });

    const response = await priceRulesClient.get(
        `/api/v1/price-rules/${ruleId}/report/logs?${params}`
    );
    return response.data;
};

// Types
export interface HourlyData {
    hour: number;
    hour_label: string;
    impressions: number;
    revenue: number;
    ecpm: number;
    pmr: number;
    clicks: number;
    ctr: number;
    requests_served: number;
    eligible_requests: number;
    custom_value: string;
}

export interface LogEntry {
    id: number;
    action: 'scaled_up' | 'scaled_down' | 'manual_change' | 'created';
    source: 'bot' | 'user';
    previous_price: number | null;
    new_price: number | null;
    metadata: {
        pmr?: number;
        pmr_target?: number;
        pmr_difference?: number;
        ecpm?: number;
        impressions?: number;
        scale_percentage?: number;
        direction?: 'up' | 'down' | 'hold';
    };
    message: string;
    created_at: string;
    hour: number;
}

export interface TimelineEntry {
    hour: number;
    hour_label: string;
    has_data: boolean;
    price: number;
    price_changes: LogEntry[];
    metrics: HourlyData;
}

export interface RuleSummary {
    total_impressions: number;
    total_revenue: number;
    total_clicks: number;
    avg_ecpm: number;
    avg_pmr: number;
    avg_ctr: number;
    hours_with_data: number;
    price_changes: number;
    scale_ups: number;
    scale_downs: number;
    min_price: number;
    max_price: number;
    best_hour: { hour: number; hour_label: string; value: number } | null;
    worst_hour: { hour: number; hour_label: string; value: number } | null;
}

export interface RuleReportResponse {
    rule: {
        id: number;
        domain_id: number;
        price: number;
        pmr_target: number;
        country: string;
        ad_unit: string;
        device: string;
        custom_key: string;
        custom_value: string;
        status: string;
    };
    period: {
        start: string;
        end: string;
    };
    timeline: TimelineEntry[];
    hourly_data: HourlyData[];
    logs: LogEntry[];
    summary: RuleSummary;
}

export interface HourlyReportResponse {
    rule_id: number;
    period: { start: string; end: string };
    hourly_data: HourlyData[];
    summary: RuleSummary;
}

export interface LogsReportResponse {
    rule_id: number;
    period: { start: string; end: string };
    logs: LogEntry[];
    total_changes: number;
}

// ========== CASCADE GRANULATION API ==========

export const getCascadeGranulations = async (): Promise<CascadeGranulation[]> => {
    const response = await priceRulesClient.get<CascadeGranulation[]>('/api/v1/cascade-granulations');
    return response.data;
};

export const createCascadeGranulation = async (data: CreateCascadeGranulationInput): Promise<CascadeGranulation> => {
    const response = await priceRulesClient.post<CascadeGranulation>('/api/v1/cascade-granulations', data);
    return response.data;
};

export const updateCascadeGranulation = async (id: number, data: UpdateCascadeGranulationInput): Promise<CascadeGranulation> => {
    const response = await priceRulesClient.put<CascadeGranulation>(`/api/v1/cascade-granulations/${id}`, data);
    return response.data;
};

export const deleteCascadeGranulation = async (id: number): Promise<void> => {
    await priceRulesClient.delete(`/api/v1/cascade-granulations/${id}`);
};

// ========== PRICE CASCADE API ==========

export const getPriceCascades = async (domainId: number): Promise<PriceCascade[]> => {
    const response = await priceRulesClient.get<PriceCascade[]>(`/api/v1/price-cascades/domain/${domainId}`);
    return response.data;
};

export const getAllPriceCascades = async (): Promise<PriceCascade[]> => {
    const response = await priceRulesClient.get<PriceCascade[]>('/api/v1/price-cascades');
    return response.data;
};

export const createPriceCascade = async (data: CreatePriceCascadeInput): Promise<PriceCascade> => {
    const response = await priceRulesClient.post<PriceCascade>('/api/v1/price-cascades', data);
    return response.data;
};

export const createPriceCascadesBatch = async (data: CreatePriceCascadesBatchInput): Promise<CreatePriceCascadesBatchResponse> => {
    const response = await priceRulesClient.post<CreatePriceCascadesBatchResponse>('/api/v1/price-cascades/batch', data);
    return response.data;
};

export const updatePriceCascade = async (cascadeId: number, data: UpdatePriceCascadeInput): Promise<PriceCascade> => {
    const response = await priceRulesClient.put<PriceCascade>(`/api/v1/price-cascades/${cascadeId}`, data);
    return response.data;
};

export const deletePriceCascade = async (cascadeId: number): Promise<void> => {
    await priceRulesClient.delete(`/api/v1/price-cascades/${cascadeId}`);
};

export const getPostsWithSlugs = async (domainId: number): Promise<PostsWithSlugsResponse> => {
    const response = await priceRulesClient.get<PostsWithSlugsResponse>(`/api/v1/price-cascades/domain/${domainId}/posts`);
    return response.data;
};

export const getCascadeSteps = async (): Promise<CascadeStepsConfig> => {
    const response = await priceRulesClient.get<CascadeStepsConfig>('/api/v1/price-cascades/steps');
    return response.data;
};

export default priceRulesClient;